import React, { useState, useEffect } from 'react';
import { Box, Grid, TextField, Button } from '@mui/material';
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon
} from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import palette from 'src/theme/palette';
import { postSettingData } from 'src/ApiServices';
import { toast } from 'react-toastify';

const SocialLinksTab = ({ tabData = [], typeId }) => {
  // Initialize formData with id and value structure
  const getInitialFormData = () => {
    const dataMap = {};
    tabData.forEach((item) => {
      dataMap[item.key] = {
        id: item.id || '',
        value: item.value || ''
      };
    });

    return {
      facebook_url: dataMap['facebook_url'] || { id: '', value: '' },
      twitter_url: dataMap['twitter_url'] || { id: '', value: '' },
      instagram_url: dataMap['instagram_url'] || { id: '', value: '' },
      linkedin_url: dataMap['linkedin_url'] || { id: '', value: '' },
      tiktok_url: dataMap['tiktok_url'] || { id: '', value: '' },
      youtube_url: dataMap['youtube_url'] || { id: '', value: '' }
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);

  // Update formData when tabData changes
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [tabData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);

    const valueArray = [
      {
        id: formData.facebook_url.id || null,
        key: 'facebook_url',
        value: formData.facebook_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.facebook_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.twitter_url.id || null,
        key: 'twitter_url',
        value: formData.twitter_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.twitter_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.instagram_url.id || null,
        key: 'instagram_url',
        value: formData.instagram_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.instagram_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.linkedin_url.id || null,
        key: 'linkedin_url',
        value: formData.linkedin_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.linkedin_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.tiktok_url.id || null,
        key: 'tiktok_url',
        value: formData.tiktok_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.tiktok_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      },
      {
        id: formData.youtube_url.id || null,
        key: 'youtube_url',
        value: formData.youtube_url.value,
        content_type: null,
        file: null,
        type_id: typeId,
        type: 'Social Links',
        created_at: formData.youtube_url.id ? new Date().toISOString() : null,
        updated_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      }
    ];

    const finalPayload = {
      type_id: typeId.toString(),
      value: JSON.stringify(valueArray)
    };

    try {
      const response = await postSettingData(finalPayload);

      const message =
        response?.message || response?.data?.message || 'Social links saved successfully!';

      toast.success(message, {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      console.error('Save Social Links Error:', error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to save social links. Please try again.';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Facebook URL"
            name="facebook_url"
            value={formData?.facebook_url?.value || ''}
            onChange={(e) => handleInputChange('facebook_url', e.target.value)}
            placeholder="https://facebook.com/yourpage"
            InputProps={{
              startAdornment: <FacebookIcon sx={{ mr: 1, color: '#1877F2' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="LinkedIn URL"
            name="linkedin_url"
            value={formData?.linkedin_url?.value || ''}
            onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
            placeholder="https://linkedin.com/company/yourcompany"
            InputProps={{
              startAdornment: <LinkedInIcon sx={{ mr: 1, color: '#0A66C2' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Twitter / X URL"
            name="twitter_url"
            value={formData?.twitter_url?.value || ''}
            onChange={(e) => handleInputChange('twitter_url', e.target.value)}
            placeholder="https://twitter.com/yourprofile"
            InputProps={{
              startAdornment: <TwitterIcon sx={{ mr: 1, color: '#000000' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="TikTok URL"
            name="tiktok_url"
            value={formData?.tiktok_url?.value || ''}
            onChange={(e) => handleInputChange('tiktok_url', e.target.value)}
            placeholder="https://tiktok.com/@yourprofile"
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{
                    mr: 1,
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: '#000000'
                  }}
                >
                  𝕏
                </Box>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Instagram URL"
            name="instagram_url"
            value={formData?.instagram_url?.value || ''}
            onChange={(e) => handleInputChange('instagram_url', e.target.value)}
            placeholder="https://instagram.com/yourprofile"
            InputProps={{
              startAdornment: <InstagramIcon sx={{ mr: 1, color: '#E4405F' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="YouTube URL"
            name="youtube_url"
            value={formData?.youtube_url?.value || ''}
            onChange={(e) => handleInputChange('youtube_url', e.target.value)}
            placeholder="https://youtube.com/yourchannel"
            InputProps={{
              startAdornment: <YouTubeIcon sx={{ mr: 1, color: '#FF0000' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SocialLinksTab;
